"use client";

import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import "./Register.css";

// Type definitions
interface Option {
  value: string;
  label: string;
}

interface LocationData {
  states: Option[];
  cities: Record<string, Option[]>;
}

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirm_password: string;
  company: string;
  company_size: string;
  industry: string;
  job_title: string;
  country: string;
  state: string;
  city: string;
  terms: boolean;
}

interface FormErrors {
  [key: string]: string;
}

const companySizeOptions: Option[] = [
  { value: "", label: "Select Company Size" },
  { value: "1-10", label: "1-10 employees" },
  { value: "11-50", label: "11-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "201-500", label: "201-500 employees" },
  { value: "501-1000", label: "501-1000 employees" },
  { value: "1000+", label: "1000+ employees" },
];

const industryOptions: Option[] = [
  { value: "", label: "Select Industry" },
  { value: "technology", label: "Technology" },
  { value: "healthcare", label: "Healthcare" },
  { value: "finance", label: "Finance" },
  { value: "education", label: "Education" },
  { value: "retail", label: "Retail" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "consulting", label: "Consulting" },
  { value: "real-estate", label: "Real Estate" },
  { value: "marketing", label: "Marketing" },
  { value: "automotive", label: "Automotive" },
  { value: "hospitality", label: "Hospitality" },
  { value: "construction", label: "Construction" },
  { value: "telecommunications", label: "Telecommunications" },
  { value: "media", label: "Media & Entertainment" },
  { value: "other", label: "Other" },
];

const countryOptions: Option[] = [
  { value: "", label: "Select Country" },
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "UK", label: "United Kingdom" },
  { value: "AU", label: "Australia" },
  { value: "IN", label: "India" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "JP", label: "Japan" },
  { value: "BR", label: "Brazil" },
  { value: "MX", label: "Mexico" },
];

const locationData: Record<string, LocationData> = {
  US: {
    states: [
      { value: "AL", label: "Alabama" },
      { value: "AK", label: "Alaska" },
      { value: "AZ", label: "Arizona" },
      { value: "AR", label: "Arkansas" },
      { value: "CA", label: "California" },
      { value: "CO", label: "Colorado" },
      { value: "CT", label: "Connecticut" },
      { value: "DE", label: "Delaware" },
      { value: "FL", label: "Florida" },
      { value: "GA", label: "Georgia" },
      { value: "HI", label: "Hawaii" },
      { value: "ID", label: "Idaho" },
      { value: "IL", label: "Illinois" },
      { value: "IN", label: "Indiana" },
      { value: "IA", label: "Iowa" },
      { value: "KS", label: "Kansas" },
      { value: "KY", label: "Kentucky" },
      { value: "LA", label: "Louisiana" },
      { value: "ME", label: "Maine" },
      { value: "MD", label: "Maryland" },
      { value: "MA", label: "Massachusetts" },
      { value: "MI", label: "Michigan" },
      { value: "MN", label: "Minnesota" },
      { value: "MS", label: "Mississippi" },
      { value: "MO", label: "Missouri" },
      { value: "MT", label: "Montana" },
      { value: "NE", label: "Nebraska" },
      { value: "NV", label: "Nevada" },
      { value: "NH", label: "New Hampshire" },
      { value: "NJ", label: "New Jersey" },
      { value: "NM", label: "New Mexico" },
      { value: "NY", label: "New York" },
      { value: "NC", label: "North Carolina" },
      { value: "ND", label: "North Dakota" },
      { value: "OH", label: "Ohio" },
      { value: "OK", label: "Oklahoma" },
      { value: "OR", label: "Oregon" },
      { value: "PA", label: "Pennsylvania" },
      { value: "RI", label: "Rhode Island" },
      { value: "SC", label: "South Carolina" },
      { value: "SD", label: "South Dakota" },
      { value: "TN", label: "Tennessee" },
      { value: "TX", label: "Texas" },
      { value: "UT", label: "Utah" },
      { value: "VT", label: "Vermont" },
      { value: "VA", label: "Virginia" },
      { value: "WA", label: "Washington" },
      { value: "WV", label: "West Virginia" },
      { value: "WI", label: "Wisconsin" },
      { value: "WY", label: "Wyoming" },
    ],
    cities: {
      CA: [
        { value: "los-angeles", label: "Los Angeles" },
        { value: "san-francisco", label: "San Francisco" },
        { value: "san-diego", label: "San Diego" },
        { value: "san-jose", label: "San Jose" },
        { value: "fresno", label: "Fresno" },
        { value: "sacramento", label: "Sacramento" },
        { value: "long-beach", label: "Long Beach" },
        { value: "oakland", label: "Oakland" },
        { value: "bakersfield", label: "Bakersfield" },
        { value: "anaheim", label: "Anaheim" },
      ],
      NY: [
        { value: "new-york-city", label: "New York City" },
        { value: "buffalo", label: "Buffalo" },
        { value: "rochester", label: "Rochester" },
        { value: "yonkers", label: "Yonkers" },
        { value: "syracuse", label: "Syracuse" },
        { value: "albany", label: "Albany" },
        { value: "new-rochelle", label: "New Rochelle" },
        { value: "mount-vernon", label: "Mount Vernon" },
      ],
      TX: [
        { value: "houston", label: "Houston" },
        { value: "san-antonio", label: "San Antonio" },
        { value: "dallas", label: "Dallas" },
        { value: "austin", label: "Austin" },
        { value: "fort-worth", label: "Fort Worth" },
        { value: "el-paso", label: "El Paso" },
        { value: "arlington", label: "Arlington" },
        { value: "corpus-christi", label: "Corpus Christi" },
      ],
      FL: [
        { value: "jacksonville", label: "Jacksonville" },
        { value: "miami", label: "Miami" },
        { value: "tampa", label: "Tampa" },
        { value: "orlando", label: "Orlando" },
        { value: "st-petersburg", label: "St. Petersburg" },
        { value: "hialeah", label: "Hialeah" },
        { value: "tallahassee", label: "Tallahassee" },
        { value: "fort-lauderdale", label: "Fort Lauderdale" },
      ],
      IL: [
        { value: "chicago", label: "Chicago" },
        { value: "aurora", label: "Aurora" },
        { value: "rockford", label: "Rockford" },
        { value: "joliet", label: "Joliet" },
        { value: "naperville", label: "Naperville" },
        { value: "springfield", label: "Springfield" },
        { value: "peoria", label: "Peoria" },
        { value: "elgin", label: "Elgin" },
      ],
      WA: [
        { value: "seattle", label: "Seattle" },
        { value: "spokane", label: "Spokane" },
        { value: "tacoma", label: "Tacoma" },
        { value: "vancouver", label: "Vancouver" },
        { value: "bellevue", label: "Bellevue" },
        { value: "kent", label: "Kent" },
        { value: "everett", label: "Everett" },
        { value: "renton", label: "Renton" },
      ],
      CO: [
        { value: "denver", label: "Denver" },
        { value: "colorado-springs", label: "Colorado Springs" },
        { value: "aurora", label: "Aurora" },
        { value: "fort-collins", label: "Fort Collins" },
        { value: "lakewood", label: "Lakewood" },
        { value: "thornton", label: "Thornton" },
        { value: "arvada", label: "Arvada" },
        { value: "westminster", label: "Westminster" },
      ],
      MA: [
        { value: "boston", label: "Boston" },
        { value: "worcester", label: "Worcester" },
        { value: "springfield", label: "Springfield" },
        { value: "lowell", label: "Lowell" },
        { value: "cambridge", label: "Cambridge" },
        { value: "new-bedford", label: "New Bedford" },
        { value: "brockton", label: "Brockton" },
        { value: "quincy", label: "Quincy" },
      ],
    },
  },
  CA: {
    states: [
      { value: "AB", label: "Alberta" },
      { value: "BC", label: "British Columbia" },
      { value: "MB", label: "Manitoba" },
      { value: "NB", label: "New Brunswick" },
      { value: "NL", label: "Newfoundland and Labrador" },
      { value: "NS", label: "Nova Scotia" },
      { value: "ON", label: "Ontario" },
      { value: "PE", label: "Prince Edward Island" },
      { value: "QC", label: "Quebec" },
      { value: "SK", label: "Saskatchewan" },
      { value: "NT", label: "Northwest Territories" },
      { value: "NU", label: "Nunavut" },
      { value: "YT", label: "Yukon" },
    ],
    cities: {
      ON: [
        { value: "toronto", label: "Toronto" },
        { value: "ottawa", label: "Ottawa" },
        { value: "mississauga", label: "Mississauga" },
        { value: "brampton", label: "Brampton" },
        { value: "hamilton", label: "Hamilton" },
        { value: "london", label: "London" },
        { value: "markham", label: "Markham" },
        { value: "vaughan", label: "Vaughan" },
      ],
      BC: [
        { value: "vancouver", label: "Vancouver" },
        { value: "surrey", label: "Surrey" },
        { value: "burnaby", label: "Burnaby" },
        { value: "richmond", label: "Richmond" },
        { value: "abbotsford", label: "Abbotsford" },
        { value: "coquitlam", label: "Coquitlam" },
        { value: "kelowna", label: "Kelowna" },
        { value: "victoria", label: "Victoria" },
      ],
      QC: [
        { value: "montreal", label: "Montreal" },
        { value: "quebec-city", label: "Quebec City" },
        { value: "laval", label: "Laval" },
        { value: "gatineau", label: "Gatineau" },
        { value: "longueuil", label: "Longueuil" },
        { value: "sherbrooke", label: "Sherbrooke" },
        { value: "saguenay", label: "Saguenay" },
        { value: "trois-rivieres", label: "Trois-Rivières" },
      ],
      AB: [
        { value: "calgary", label: "Calgary" },
        { value: "edmonton", label: "Edmonton" },
        { value: "red-deer", label: "Red Deer" },
        { value: "lethbridge", label: "Lethbridge" },
        { value: "medicine-hat", label: "Medicine Hat" },
        { value: "grande-prairie", label: "Grande Prairie" },
        { value: "airdrie", label: "Airdrie" },
        { value: "spruce-grove", label: "Spruce Grove" },
      ],
    },
  },
  UK: {
    states: [
      { value: "ENG", label: "England" },
      { value: "SCT", label: "Scotland" },
      { value: "WLS", label: "Wales" },
      { value: "NIR", label: "Northern Ireland" },
    ],
    cities: {
      ENG: [
        { value: "london", label: "London" },
        { value: "birmingham", label: "Birmingham" },
        { value: "manchester", label: "Manchester" },
        { value: "leeds", label: "Leeds" },
        { value: "liverpool", label: "Liverpool" },
        { value: "sheffield", label: "Sheffield" },
        { value: "bristol", label: "Bristol" },
        { value: "newcastle", label: "Newcastle" },
        { value: "nottingham", label: "Nottingham" },
        { value: "leicester", label: "Leicester" },
      ],
      SCT: [
        { value: "glasgow", label: "Glasgow" },
        { value: "edinburgh", label: "Edinburgh" },
        { value: "aberdeen", label: "Aberdeen" },
        { value: "dundee", label: "Dundee" },
        { value: "stirling", label: "Stirling" },
        { value: "perth", label: "Perth" },
        { value: "inverness", label: "Inverness" },
        { value: "paisley", label: "Paisley" },
      ],
      WLS: [
        { value: "cardiff", label: "Cardiff" },
        { value: "swansea", label: "Swansea" },
        { value: "newport", label: "Newport" },
        { value: "wrexham", label: "Wrexham" },
        { value: "barry", label: "Barry" },
        { value: "caerphilly", label: "Caerphilly" },
        { value: "bridgend", label: "Bridgend" },
        { value: "neath", label: "Neath" },
      ],
      NIR: [
        { value: "belfast", label: "Belfast" },
        { value: "derry", label: "Derry" },
        { value: "lisburn", label: "Lisburn" },
        { value: "newtownabbey", label: "Newtownabbey" },
        { value: "bangor", label: "Bangor" },
        { value: "craigavon", label: "Craigavon" },
        { value: "castlereagh", label: "Castlereagh" },
        { value: "ballymena", label: "Ballymena" },
      ],
    },
  },
  AU: {
    states: [
      { value: "NSW", label: "New South Wales" },
      { value: "VIC", label: "Victoria" },
      { value: "QLD", label: "Queensland" },
      { value: "WA", label: "Western Australia" },
      { value: "SA", label: "South Australia" },
      { value: "TAS", label: "Tasmania" },
      { value: "ACT", label: "Australian Capital Territory" },
      { value: "NT", label: "Northern Territory" },
    ],
    cities: {
      NSW: [
        { value: "sydney", label: "Sydney" },
        { value: "newcastle", label: "Newcastle" },
        { value: "wollongong", label: "Wollongong" },
        { value: "central-coast", label: "Central Coast" },
        { value: "maitland", label: "Maitland" },
        { value: "albury", label: "Albury" },
        { value: "wagga-wagga", label: "Wagga Wagga" },
        { value: "port-macquarie", label: "Port Macquarie" },
      ],
      VIC: [
        { value: "melbourne", label: "Melbourne" },
        { value: "geelong", label: "Geelong" },
        { value: "ballarat", label: "Ballarat" },
        { value: "bendigo", label: "Bendigo" },
        { value: "frankston", label: "Frankston" },
        { value: "latrobe", label: "Latrobe" },
        { value: "shepparton", label: "Shepparton" },
        { value: "wodonga", label: "Wodonga" },
      ],
      QLD: [
        { value: "brisbane", label: "Brisbane" },
        { value: "gold-coast", label: "Gold Coast" },
        { value: "sunshine-coast", label: "Sunshine Coast" },
        { value: "townsville", label: "Townsville" },
        { value: "cairns", label: "Cairns" },
        { value: "toowoomba", label: "Toowoomba" },
        { value: "mackay", label: "Mackay" },
        { value: "rockhampton", label: "Rockhampton" },
      ],
      WA: [
        { value: "perth", label: "Perth" },
        { value: "mandurah", label: "Mandurah" },
        { value: "bunbury", label: "Bunbury" },
        { value: "albany", label: "Albany" },
        { value: "kalgoorlie", label: "Kalgoorlie" },
        { value: "geraldton", label: "Geraldton" },
        { value: "broome", label: "Broome" },
        { value: "port-hedland", label: "Port Hedland" },
      ],
    },
  },
  IN: {
    states: [
      { value: "MH", label: "Maharashtra" },
      { value: "DL", label: "Delhi" },
      { value: "KA", label: "Karnataka" },
      { value: "TN", label: "Tamil Nadu" },
      { value: "WB", label: "West Bengal" },
      { value: "GJ", label: "Gujarat" },
      { value: "RJ", label: "Rajasthan" },
      { value: "UP", label: "Uttar Pradesh" },
      { value: "MP", label: "Madhya Pradesh" },
      { value: "TG", label: "Telangana" },
      { value: "AP", label: "Andhra Pradesh" },
      { value: "KL", label: "Kerala" },
      { value: "OR", label: "Odisha" },
      { value: "PB", label: "Punjab" },
      { value: "HR", label: "Haryana" },
    ],
    cities: {
      MH: [
        { value: "mumbai", label: "Mumbai" },
        { value: "pune", label: "Pune" },
        { value: "nagpur", label: "Nagpur" },
        { value: "nashik", label: "Nashik" },
        { value: "aurangabad", label: "Aurangabad" },
        { value: "solapur", label: "Solapur" },
        { value: "amravati", label: "Amravati" },
        { value: "kolhapur", label: "Kolhapur" },
      ],
      DL: [
        { value: "new-delhi", label: "New Delhi" },
        { value: "delhi", label: "Delhi" },
        { value: "gurgaon", label: "Gurgaon" },
        { value: "noida", label: "Noida" },
        { value: "faridabad", label: "Faridabad" },
        { value: "ghaziabad", label: "Ghaziabad" },
        { value: "greater-noida", label: "Greater Noida" },
        { value: "dwarka", label: "Dwarka" },
      ],
      KA: [
        { value: "bangalore", label: "Bangalore" },
        { value: "mysore", label: "Mysore" },
        { value: "hubli", label: "Hubli" },
        { value: "mangalore", label: "Mangalore" },
        { value: "belgaum", label: "Belgaum" },
        { value: "gulbarga", label: "Gulbarga" },
        { value: "davanagere", label: "Davanagere" },
        { value: "bellary", label: "Bellary" },
      ],
      TN: [
        { value: "chennai", label: "Chennai" },
        { value: "coimbatore", label: "Coimbatore" },
        { value: "madurai", label: "Madurai" },
        { value: "tiruchirappalli", label: "Tiruchirappalli" },
        { value: "salem", label: "Salem" },
        { value: "tirunelveli", label: "Tirunelveli" },
        { value: "erode", label: "Erode" },
        { value: "vellore", label: "Vellore" },
      ],
      TG: [
        { value: "hyderabad", label: "Hyderabad" },
        { value: "warangal", label: "Warangal" },
        { value: "nizamabad", label: "Nizamabad" },
        { value: "karimnagar", label: "Karimnagar" },
        { value: "ramagundam", label: "Ramagundam" },
        { value: "khammam", label: "Khammam" },
        { value: "mahbubnagar", label: "Mahbubnagar" },
        { value: "nalgonda", label: "Nalgonda" },
      ],
      MP: [
        { value: "bhopal", label: "Bhopal" },
        { value: "indore", label: "Indore" },
        { value: "gwalior", label: "Gwalior" },
        { value: "jabalpur", label: "Jabalpur" },
        { value: "ujjain", label: "Ujjain" },
        { value: "sagar", label: "Sagar" },
        { value: "dewas", label: "Dewas" },
        { value: "satna", label: "Satna" },
      ],
      UP: [
        { value: "lucknow", label: "Lucknow" },
        { value: "kanpur", label: "Kanpur" },
        { value: "ghaziabad", label: "Ghaziabad" },
        { value: "agra", label: "Agra" },
        { value: "meerut", label: "Meerut" },
        { value: "varanasi", label: "Varanasi" },
        { value: "allahabad", label: "Allahabad" },
        { value: "bareilly", label: "Bareilly" },
        { value: "gorakhpur", label: "Gorakhpur" },
        { value: "deoria", label: "Deoria" },
      ],
      RJ: [
        { value: "jaipur", label: "Jaipur" },
        { value: "jodhpur", label: "Jodhpur" },
        { value: "kota", label: "Kota" },
        { value: "bikaner", label: "Bikaner" },
        { value: "udaipur", label: "Udaipur" },
        { value: "ajmer", label: "Ajmer" },
        { value: "bhilwara", label: "Bhilwara" },
        { value: "alwar", label: "Alwar" },
      ],
      AP: [
        { value: "amaravati", label: "Amaravati" },
        { value: "visakhapatnam", label: "Visakhapatnam" },
        { value: "vijayawada", label: "Vijayawada" },
        { value: "guntur", label: "Guntur" },
        { value: "nellore", label: "Nellore" },
        { value: "kurnool", label: "Kurnool" },
        { value: "rajahmundry", label: "Rajahmundry" },
        { value: "tirupati", label: "Tirupati" },
      ],
      KL: [
        { value: "thiruvananthapuram", label: "Thiruvananthapuram" },
        { value: "kochi", label: "Kochi" },
        { value: "kozhikode", label: "Kozhikode" },
        { value: "thrissur", label: "Thrissur" },
        { value: "kollam", label: "Kollam" },
        { value: "palakkad", label: "Palakkad" },
        { value: "alappuzha", label: "Alappuzha" },
        { value: "malappuram", label: "Malappuram" },
      ],
      OR: [
        { value: "bhubaneswar", label: "Bhubaneswar" },
        { value: "cuttack", label: "Cuttack" },
        { value: "rourkela", label: "Rourkela" },
        { value: "berhampur", label: "Berhampur" },
        { value: "sambalpur", label: "Sambalpur" },
        { value: "puri", label: "Puri" },
        { value: "balasore", label: "Balasore" },
        { value: "bhadrak", label: "Bhadrak" },
      ],
      PB: [
        { value: "amritsar", label: "Amritsar" },
        { value: "ludhiana", label: "Ludhiana" },
        { value: "jalandhar", label: "Jalandhar" },
        { value: "patiala", label: "Patiala" },
        { value: "bathinda", label: "Bathinda" },
        { value: "mohali", label: "Mohali" },
        { value: "firozpur", label: "Firozpur" },
        { value: "hoshiarpur", label: "Hoshiarpur" },
      ],
      WB: [
        { value: "kolkata", label: "Kolkata" },
        { value: "howrah", label: "Howrah" },
        { value: "durgapur", label: "Durgapur" },
        { value: "asansol", label: "Asansol" },
        { value: "siliguri", label: "Siliguri" },
        { value: "malda", label: "Malda" },
        { value: "barrackpore", label: "Barrackpore" },
        { value: "kharagpur", label: "Kharagpur" },
      ],
      HR: [
        { value: "faridabad", label: "Faridabad" },
        { value: "gurgaon", label: "Gurgaon" },
        { value: "panipat", label: "Panipat" },
        { value: "ambala", label: "Ambala" },
        { value: "yamunanagar", label: "Yamunanagar" },
        { value: "rohtak", label: "Rohtak" },
        { value: "hisar", label: "Hisar" },
        { value: "karnal", label: "Karnal" },
      ],
      GJ: [
        { value: "ahmedabad", label: "Ahmedabad" },
        { value: "surat", label: "Surat" },
        { value: "vadodara", label: "Vadodara" },
        { value: "rajkot", label: "Rajkot" },
        { value: "bhavnagar", label: "Bhavnagar" },
        { value: "jamnagar", label: "Jamnagar" },
        { value: "gandhinagar", label: "Gandhinagar" },
        { value: "junagadh", label: "Junagadh" },
      ],
    },
  },
  DE: {
    states: [
      { value: "BW", label: "Baden-Württemberg" },
      { value: "BY", label: "Bavaria" },
      { value: "BE", label: "Berlin" },
      { value: "BB", label: "Brandenburg" },
      { value: "HB", label: "Bremen" },
      { value: "HH", label: "Hamburg" },
      { value: "HE", label: "Hesse" },
      { value: "NI", label: "Lower Saxony" },
      { value: "MV", label: "Mecklenburg-Vorpommern" },
      { value: "NW", label: "North Rhine-Westphalia" },
      { value: "RP", label: "Rhineland-Palatinate" },
      { value: "SL", label: "Saarland" },
      { value: "SN", label: "Saxony" },
      { value: "ST", label: "Saxony-Anhalt" },
      { value: "SH", label: "Schleswig-Holstein" },
      { value: "TH", label: "Thuringia" },
    ],
    cities: {
      NW: [
        { value: "cologne", label: "Cologne" },
        { value: "dusseldorf", label: "Düsseldorf" },
        { value: "dortmund", label: "Dortmund" },
        { value: "essen", label: "Essen" },
        { value: "duisburg", label: "Duisburg" },
        { value: "bochum", label: "Bochum" },
        { value: "wuppertal", label: "Wuppertal" },
        { value: "bielefeld", label: "Bielefeld" },
      ],
      BY: [
        { value: "munich", label: "Munich" },
        { value: "nuremberg", label: "Nuremberg" },
        { value: "augsburg", label: "Augsburg" },
        { value: "regensburg", label: "Regensburg" },
        { value: "ingolstadt", label: "Ingolstadt" },
        { value: "wurzburg", label: "Würzburg" },
        { value: "fuerth", label: "Fürth" },
        { value: "erlangen", label: "Erlangen" },
      ],
      BE: [{ value: "berlin", label: "Berlin" }],
      HH: [{ value: "hamburg", label: "Hamburg" }],
    },
  },
  FR: {
    states: [
      { value: "IDF", label: "Île-de-France" },
      { value: "ARA", label: "Auvergne-Rhône-Alpes" },
      { value: "HDF", label: "Hauts-de-France" },
      { value: "OCC", label: "Occitanie" },
      { value: "NAQ", label: "Nouvelle-Aquitaine" },
      { value: "GES", label: "Grand Est" },
      { value: "PDL", label: "Pays de la Loire" },
      { value: "BRE", label: "Bretagne" },
      { value: "PAC", label: "Provence-Alpes-Côte d'Azur" },
      { value: "NOR", label: "Normandie" },
      { value: "BFC", label: "Bourgogne-Franche-Comté" },
      { value: "CVL", label: "Centre-Val de Loire" },
      { value: "COR", label: "Corsica" },
    ],
    cities: {
      IDF: [
        { value: "paris", label: "Paris" },
        { value: "boulogne-billancourt", label: "Boulogne-Billancourt" },
        { value: "saint-denis", label: "Saint-Denis" },
        { value: "argenteuil", label: "Argenteuil" },
        { value: "montreuil", label: "Montreuil" },
        { value: "créteil", label: "Créteil" },
        { value: "nanterre", label: "Nanterre" },
        { value: "versailles", label: "Versailles" },
      ],
      ARA: [
        { value: "lyon", label: "Lyon" },
        { value: "saint-etienne", label: "Saint-Étienne" },
        { value: "grenoble", label: "Grenoble" },
        { value: "villeurbanne", label: "Villeurbanne" },
        { value: "clermont-ferrand", label: "Clermont-Ferrand" },
        { value: "valence", label: "Valence" },
        { value: "chambery", label: "Chambéry" },
        { value: "annecy", label: "Annecy" },
      ],
      PAC: [
        { value: "marseille", label: "Marseille" },
        { value: "nice", label: "Nice" },
        { value: "toulon", label: "Toulon" },
        { value: "aix-en-provence", label: "Aix-en-Provence" },
        { value: "avignon", label: "Avignon" },
        { value: "antibes", label: "Antibes" },
        { value: "cannes", label: "Cannes" },
        { value: "la-seyne-sur-mer", label: "La Seyne-sur-Mer" },
      ],
    },
  },
  JP: {
    states: [
      { value: "13", label: "Tokyo" },
      { value: "27", label: "Osaka" },
      { value: "14", label: "Kanagawa" },
      { value: "23", label: "Aichi" },
      { value: "11", label: "Saitama" },
      { value: "12", label: "Chiba" },
      { value: "28", label: "Hyogo" },
      { value: "01", label: "Hokkaido" },
      { value: "40", label: "Fukuoka" },
      { value: "22", label: "Shizuoka" },
      { value: "08", label: "Ibaraki" },
      { value: "26", label: "Kyoto" },
      { value: "34", label: "Hiroshima" },
      { value: "04", label: "Miyagi" },
      { value: "15", label: "Niigata" },
    ],
    cities: {
      "13": [
        { value: "tokyo", label: "Tokyo" },
        { value: "shibuya", label: "Shibuya" },
        { value: "shinjuku", label: "Shinjuku" },
        { value: "harajuku", label: "Harajuku" },
        { value: "ginza", label: "Ginza" },
        { value: "akihabara", label: "Akihabara" },
        { value: "roppongi", label: "Roppongi" },
        { value: "asakusa", label: "Asakusa" },
      ],
      "27": [
        { value: "osaka", label: "Osaka" },
        { value: "sakai", label: "Sakai" },
        { value: "higashiosaka", label: "Higashiosaka" },
        { value: "hirakata", label: "Hirakata" },
        { value: "toyonaka", label: "Toyonaka" },
        { value: "takatsuki", label: "Takatsuki" },
        { value: "suita", label: "Suita" },
        { value: "yao", label: "Yao" },
      ],
      "14": [
        { value: "yokohama", label: "Yokohama" },
        { value: "kawasaki", label: "Kawasaki" },
        { value: "sagamihara", label: "Sagamihara" },
        { value: "fujisawa", label: "Fujisawa" },
        { value: "yokosuka", label: "Yokosuka" },
        { value: "hiratsuka", label: "Hiratsuka" },
        { value: "chigasaki", label: "Chigasaki" },
        { value: "machida", label: "Machida" },
      ],
    },
  },
  BR: {
    states: [
      { value: "SP", label: "São Paulo" },
      { value: "RJ", label: "Rio de Janeiro" },
      { value: "MG", label: "Minas Gerais" },
      { value: "BA", label: "Bahia" },
      { value: "PR", label: "Paraná" },
      { value: "RS", label: "Rio Grande do Sul" },
      { value: "PE", label: "Pernambuco" },
      { value: "CE", label: "Ceará" },
      { value: "PA", label: "Pará" },
      { value: "SC", label: "Santa Catarina" },
      { value: "GO", label: "Goiás" },
      { value: "MA", label: "Maranhão" },
      { value: "ES", label: "Espírito Santo" },
      { value: "PB", label: "Paraíba" },
      { value: "AM", label: "Amazonas" },
    ],
    cities: {
      SP: [
        { value: "sao-paulo", label: "São Paulo" },
        { value: "guarulhos", label: "Guarulhos" },
        { value: "campinas", label: "Campinas" },
        { value: "sao-bernardo-do-campo", label: "São Bernardo do Campo" },
        { value: "santo-andre", label: "Santo André" },
        { value: "osasco", label: "Osasco" },
        { value: "ribeirao-preto", label: "Ribeirão Preto" },
        { value: "sorocaba", label: "Sorocaba" },
      ],
      RJ: [
        { value: "rio-de-janeiro", label: "Rio de Janeiro" },
        { value: "sao-goncalo", label: "São Gonçalo" },
        { value: "duque-de-caxias", label: "Duque de Caxias" },
        { value: "nova-iguacu", label: "Nova Iguaçu" },
        { value: "niteroi", label: "Niterói" },
        { value: "campos-dos-goytacazes", label: "Campos dos Goytacazes" },
        { value: "belford-roxo", label: "Belford Roxo" },
        { value: "sao-joao-de-meriti", label: "São João de Meriti" },
      ],
      MG: [
        { value: "belo-horizonte", label: "Belo Horizonte" },
        { value: "uberlandia", label: "Uberlândia" },
        { value: "contagem", label: "Contagem" },
        { value: "juiz-de-fora", label: "Juiz de Fora" },
        { value: "betim", label: "Betim" },
        { value: "montes-claros", label: "Montes Claros" },
        { value: "ribeirao-das-neves", label: "Ribeirão das Neves" },
        { value: "uberaba", label: "Uberaba" },
      ],
    },
  },
  MX: {
    states: [
      { value: "CMX", label: "Mexico City" },
      { value: "JAL", label: "Jalisco" },
      { value: "NLE", label: "Nuevo León" },
      { value: "PUE", label: "Puebla" },
      { value: "GTO", label: "Guanajuato" },
      { value: "CHH", label: "Chihuahua" },
      { value: "VER", label: "Veracruz" },
      { value: "BCN", label: "Baja California" },
      { value: "SON", label: "Sonora" },
      { value: "COA", label: "Coahuila" },
      { value: "SIN", label: "Sinaloa" },
      { value: "TAM", label: "Tamaulipas" },
      { value: "MEX", label: "State of Mexico" },
      { value: "HID", label: "Hidalgo" },
      { value: "MIC", label: "Michoacán" },
    ],
    cities: {
      CMX: [{ value: "mexico-city", label: "Mexico City" }],
      JAL: [
        { value: "guadalajara", label: "Guadalajara" },
        { value: "zapopan", label: "Zapopan" },
        { value: "tlaquepaque", label: "Tlaquepaque" },
        { value: "tonala", label: "Tonalá" },
        { value: "tlajomulco", label: "Tlajomulco" },
        { value: "puerto-vallarta", label: "Puerto Vallarta" },
        { value: "el-salto", label: "El Salto" },
        { value: "tepatitlan", label: "Tepatitlán" },
      ],
      NLE: [
        { value: "monterrey", label: "Monterrey" },
        { value: "guadalupe", label: "Guadalupe" },
        { value: "san-nicolas", label: "San Nicolás" },
        { value: "apodaca", label: "Apodaca" },
        { value: "general-escobedo", label: "General Escobedo" },
        { value: "santa-catarina", label: "Santa Catarina" },
        { value: "san-pedro", label: "San Pedro" },
        { value: "garcia", label: "García" },
      ],
      PUE: [
        { value: "puebla", label: "Puebla" },
        { value: "tehuacan", label: "Tehuacán" },
        { value: "san-martin", label: "San Martín" },
        { value: "atlixco", label: "Atlixco" },
        { value: "san-pedro-cholula", label: "San Pedro Cholula" },
        { value: "amozoc", label: "Amozoc" },
        { value: "teziutlan", label: "Teziutlán" },
        { value: "cuautlancingo", label: "Cuautlancingo" },
      ],
    },
  },
}

const Register: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    company: "",
    company_size: "",
    industry: "",
    job_title: "",
    country: "",
    state: "",
    city: "",
    terms: false,
  });

  const [availableStates, setAvailableStates] = useState<Option[]>([]);
  const [availableCities, setAvailableCities] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const { error, success } = useToast();
  const { register } = useAuth();
  const navigate = useNavigate();

  // Update available states when country changes
  useEffect(() => {
    if (formData.country && locationData[formData.country]) {
      setAvailableStates(locationData[formData.country].states);
      // Reset state and city when country changes
      setFormData((prev) => ({ ...prev, state: "", city: "" }));
      setAvailableCities([]);
    } else {
      setAvailableStates([]);
      setAvailableCities([]);
    }
  }, [formData.country]);

  // Update available cities when state changes
  useEffect(() => {
    if (
      formData.country &&
      formData.state &&
      locationData[formData.country]?.cities?.[formData.state]
    ) {
      setAvailableCities(locationData[formData.country].cities[formData.state]);
      // Reset city when state changes
      setFormData((prev) => ({ ...prev, city: "" }));
    } else {
      setAvailableCities([]);
    }
  }, [formData.country, formData.state]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Required field validation
    if (!formData.first_name.trim())
      newErrors.first_name = "First name is required";
    if (!formData.last_name.trim())
      newErrors.last_name = "Last name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.password) newErrors.password = "Password is required";
    if (!formData.confirm_password)
      newErrors.confirm_password = "Please confirm your password";
    if (!formData.company.trim())
      newErrors.company = "Company name is required";
    if (!formData.company_size)
      newErrors.company_size = "Company size is required";
    if (!formData.industry) newErrors.industry = "Industry is required";
    if (!formData.job_title.trim())
      newErrors.job_title = "Job title is required";
    if (!formData.country) newErrors.country = "Country is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.city) newErrors.city = "City is required";
    if (!formData.terms)
      newErrors.terms = "You must agree to the terms and conditions";

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (formData.password && formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    // Password confirmation validation
    if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords don't match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    if (!formData.terms) {
      setIsLoading(false);
      error("Please agree to the terms and conditions");
      return;
    }

    try {
      await register(formData);
      success(
        "Registration successful! Please check your email to verify your account."
      );
      navigate("/login");
      // Reset form
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        confirm_password: "",
        company: "",
        company_size: "",
        industry: "",
        job_title: "",
        country: "",
        state: "",
        city: "",
        terms: false,
      });
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error("An error occurred:", err.message);
        error(err.message || "Registration failed");
      } else {
        console.info("An unexpected err occurred:", err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form or navigate away
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      confirm_password: "",
      company: "",
      company_size: "",
      industry: "",
      job_title: "",
      country: "",
      state: "",
      city: "",
      terms: false,
    });
    setErrors({});
    navigate("/login");
  };

  return (
    <div className="register-page">
      {/* Background with pattern */}
      <div className="login-background">
        <div className="login-background-pattern"></div>
      </div>

      {/* Header */}
      <div className="login-header">
        <Link to="/" className="register-logo-link">
          <div className="logo">
            <div className="logo-icon">
              <div className="login-logo-icon">⚡</div>
            </div>
            <span className="logo-text">Netzsync</span>
          </div>
        </Link>
      </div>
      <div className="register-background">
        <div className="background-gradient"></div>
      </div>

      <div className="register-modal">
        <div className="register-header">
          <h2>Hi</h2>
          <p>Let's start your trial for free!</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-row">
            <div className="form-group">
              <input
                type="text"
                id="first_name"
                name="first_name"
                placeholder="First Name*"
                value={formData.first_name}
                onChange={handleChange}
                className={`form-input ${errors.first_name ? "error" : ""}`}
                disabled={isLoading}
              />
              {errors.first_name && (
                <span className="error-message">{errors.first_name}</span>
              )}
            </div>
            <div className="form-group">
              <input
                type="text"
                id="last_name"
                name="last_name"
                placeholder="Last Name*"
                value={formData.last_name}
                onChange={handleChange}
                className={`form-input ${errors.last_name ? "error" : ""}`}
                disabled={isLoading}
              />
              {errors.last_name && (
                <span className="error-message">{errors.last_name}</span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Business Email*"
                value={formData.email}
                onChange={handleChange}
                className={`form-input ${errors.email ? "error" : ""}`}
                disabled={isLoading}
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              {/* <div className="form-group full-width"> */}
              <input
                type="text"
                id="company"
                name="company"
                placeholder="Company Name*"
                value={formData.company}
                onChange={handleChange}
                className={`form-input ${errors.company ? "error" : ""}`}
                disabled={isLoading}
              />
              {errors.company && (
                <span className="error-message">{errors.company}</span>
              )}
            </div>
          </div>
          {/* <div className="form-group full-width"> */}

          <div className="form-row">
            <div className="form-group">
              <select
                name="company_size"
                value={formData.company_size}
                onChange={handleChange}
                className={`form-input form-select ${
                  errors.company_size ? "error" : ""
                }`}
                disabled={isLoading}
              >
                <option value="">Company Size*</option>
                {companySizeOptions.slice(1).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.company_size && (
                <span className="error-message">{errors.company_size}</span>
              )}
            </div>
            <div className="form-group">
              <select
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                className={`form-input form-select ${
                  errors.industry ? "error" : ""
                }`}
                disabled={isLoading}
              >
                <option value="">Industry*</option>
                {industryOptions.slice(1).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.industry && (
                <span className="error-message">{errors.industry}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <input
                type="text"
                id="job_title"
                name="job_title"
                placeholder="Job Title*"
                value={formData.job_title}
                onChange={handleChange}
                className={`form-input ${errors.job_title ? "error" : ""}`}
                disabled={isLoading}
              />
              {errors.job_title && (
                <span className="error-message">{errors.job_title}</span>
              )}
            </div>
            <div className="form-group">
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className={`form-input form-select ${
                  errors.country ? "error" : ""
                }`}
                disabled={isLoading}
              >
                <option value="">Country*</option>
                {countryOptions.slice(1).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.country && (
                <span className="error-message">{errors.country}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className={`form-input form-select ${
                  errors.state ? "error" : ""
                } ${!formData.country ? "disabled" : ""}`}
                disabled={!formData.country || isLoading}
              >
                <option value="">State*</option>
                {availableStates.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.state && (
                <span className="error-message">{errors.state}</span>
              )}
            </div>
            <div className="form-group">
              <select
                name="city"
                value={formData.city}
                onChange={handleChange}
                className={`form-input form-select ${
                  errors.city ? "error" : ""
                } ${!formData.state ? "disabled" : ""}`}
                disabled={!formData.state || isLoading}
              >
                <option value="">City*</option>
                {availableCities.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.city && (
                <span className="error-message">{errors.city}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <input
                type="password"
                id="password"
                name="password"
                placeholder="Set Password*"
                value={formData.password}
                onChange={handleChange}
                className={`form-input ${errors.password ? "error" : ""}`}
                disabled={isLoading}
              />
              {errors.password && (
                <span className="error-message">{errors.password}</span>
              )}
            </div>
            <div className="form-group">
              <input
                type="password"
                id="confirm_password"
                name="confirm_password"
                placeholder="Confirm Password*"
                value={formData.confirm_password}
                onChange={handleChange}
                className={`form-input ${
                  errors.confirm_password ? "error" : ""
                }`}
                disabled={isLoading}
              />
              {errors.confirm_password && (
                <span className="error-message">{errors.confirm_password}</span>
              )}
            </div>
          </div>

          <div className="terms-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                id="terms"
                name="terms"
                checked={formData.terms}
                onChange={handleChange}
                disabled={isLoading}
              />
              <span className="checkmark"></span>
              By clicking Submit, you agree to our Terms, Privacy Policy and Cookies Policy. You may receive email notifications from us and can opt out at any time.
            </label>
            {errors.terms && (
              <span className="error-message">{errors.terms}</span>
            )}
          </div>

          <div className="form-buttons">
            <button
              type="button"
              className="cancel-button"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="register-submit-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="loading-spinner"></span>
                  Submitting...
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
